import { ChangeDetectionStrategy, Component, HostBinding, OnInit, ViewEncapsulation, inject } from '@angular/core'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { distinctUntilChanged, map, filter, startWith, switchMap } from 'rxjs/operators'
import { combineLatest } from 'rxjs'
import { AuthApiService } from 'src/api/auth'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { AngularJSComponent } from './angularjs/angularjs.component'
import { Title } from '@angular/platform-browser'
import { HybridRouter } from './angularjs/hybrid-router.service'
import { serializeUrl } from './app-routing.module'
import { SwUpdate } from '@angular/service-worker'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { DelightedNpsService } from 'src/services/delighted-nps.service'

@Component({
  selector: 'tc-root',
  template: `
    <ngx-loading-bar></ngx-loading-bar>
    <tc-header></tc-header>
    <router-outlet class="tc-angular-page"></router-outlet>
    <div ui-view ng-cloak class="ng1-router-outlet"></div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [`
    body:not(.tc-login-layout) tc-login,
    .tc-login-layout tc-header,
    .ng1-route-inactive .ng1-router-outlet,
    .ng1-route-active > .ng-star-inserted {
      display: none !important;
    }
  `],
  standalone: false
})
export class AppComponent extends OnDestroyMixin implements OnInit {
  private router = inject(HybridRouter)
  private angularRouter = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private titleService = inject(Title)
  private authService = inject(AuthApiService)
  private swUpdate = inject(SwUpdate)
  private toaster = inject(ToasterService)
  private delightedNps = inject(DelightedNpsService)

  @HostBinding('class.ng1-route-active')
  ngRouteActive = false

  @HostBinding('class.ng1-route-inactive')
  ngRouteInactive = false

  loginLayout$ = this.router.urlChanged$.pipe(
    map(x => serializeUrl(x.url) === 'login' || serializeUrl(x.url) === 'forgot-password' || serializeUrl(x.url) === 'reset-password'),
    startWith(this.isLoginPage()),
    distinctUntilChanged())

  constructor() {
    super()
    this.authService.setRealUser()
    this.loginLayout$.pipe(untilComponentDestroyed(this)).subscribe(loginLayout => {
      if (loginLayout) document.body.classList.add('tc-login-layout')
      else document.body.classList.remove('tc-login-layout')
    })
    this.router.angularChanged$.pipe(untilComponentDestroyed(this)).subscribe(ng1 => {
      this.ngRouteActive = ng1
      this.ngRouteInactive = !ng1
    })

    // Set page title based on active route data (skip AngularJS fallback routes)
    this.angularRouter.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter(route => route.component !== AngularJSComponent),
      switchMap(route => route.data),
      untilComponentDestroyed(this),
    ).subscribe(data => {
      const pageTitle = data?.title;
      this.titleService.setTitle(pageTitle ? `${pageTitle} | TradeCafe` : 'TradeCafe');
    })

    // Set page title for AngularJS routes
    this.router.urlChanged$.pipe(
      filter(e => e.ng1 && !!e.ng1StateData?.title),
      untilComponentDestroyed(this),
    ).subscribe(e => {
      this.titleService.setTitle(`${e.ng1StateData.title} | TradeCafe`);
    })

    // Initialize Delighted NPS when user is authenticated and not on login page
    combineLatest([
      this.authService.currentUser$,
      this.loginLayout$
    ]).pipe(
      filter(([user, isLoginLayout]) => !!user && !isLoginLayout),
      untilComponentDestroyed(this)
    ).subscribe(() => {
      this.delightedNps.initialize()
    })
  }

  private isLoginPage(): boolean {
    const url = this.angularRouter.url || window.location.pathname
    const serialized = serializeUrl(url)
    return serialized === 'login' || serialized === 'forgot-password' || serialized === 'reset-password'
  }

  ngOnInit(): void {
    if (this.swUpdate.isEnabled) {
      this.subscribeToSwUpdates();
    }
  }

  checkForSwUpdates() {
    this.swUpdate.checkForUpdate().then(() => {
      console.log(`[SwUpdate]: Looking for updates`);
    });
  }

  subscribeToSwUpdates() {
    this.swUpdate.versionUpdates.subscribe(event => {
      console.log('SwEvent:', event)
      if (event.type === 'VERSION_READY') {
        console.log('[SwUpdate]: Current version is', event.currentVersion);
        console.log('[SwUpdate]: Available version is', event.latestVersion);
        this.toaster.pop({
          type: 'warning',
          isPersistent: true,
          html: '<div style="display: flex; justify-content: space-between; align-items: center;"><div>New Version Available</div><button style="color: white; background: none; border: none; cursor: pointer; font-weight: bold;" onclick="window.location.reload();">Update</button></div>'
        })
      }
    });
  }
}
